import React from 'react';
import { Flex, Button, Checkbox } from 'design-system-web';
import { Alert } from 'components/lds';
import routes from 'lane-shared/config/routes';
import history from 'helpers/history';
import { Channel } from 'packages/lane-shared/types/ChannelType';
import { TranslationKeys } from 'localization';
import { useTranslation } from 'react-i18next';
import { H5 } from 'components/typography';
import { useWorkOrdersPaymentsManagedExternallyEnabled } from 'react-hooks';
import styles from './BillingTab.scss';
import { WorkOrderModuleSettings } from 'lane-shared/domains/workOrder';

import { getInvoicingOptionsEnabledValue } from '../../../../helpers/getInvoicingOptionsEnabledValue';

const TRANSLATION_KEYS: { [key: string]: TranslationKeys } = Object.freeze({
  billingPaymentsHeader:
    'web.admin.serviceRequest.settings.billing.billingPayments',
  productsServicesButton:
    'web.admin.serviceRequest.settings.billing.productsAndServicesButton',
  invoicesHeader: 'web.admin.serviceRequest.settings.billing.invoices',
  enableInvoicingCheckbox:
    'web.admin.serviceRequest.settings.billing.enableInvoicing',
  enableInvoicingDisabledWarning:
    'web.admin.serviceRequest.settings.billing.enableInvoicing.disabled.warning',
  paymentsManagedExternallyCheckbox:
    'web.admin.serviceRequest.settings.billing.paymentsManagedExternally',
});

type Props = {
  channel: Channel;
  settings: WorkOrderModuleSettings;
  isBillingInvoicingDisabled: boolean;
  isPaymentsManagedExternally: boolean;
  onSettingsUpdated: (settings: WorkOrderModuleSettings) => void;
  onPaymentsManagedExternallyUpdated: (isEnabled: boolean) => void;
};

export const BillingTab = ({
  channel,
  settings,
  isBillingInvoicingDisabled,
  isPaymentsManagedExternally,
  onSettingsUpdated,
  onPaymentsManagedExternallyUpdated,
}: Props) => {
  const { t } = useTranslation();

  const isInvoiceOptionsEnabled = getInvoicingOptionsEnabledValue(
    isBillingInvoicingDisabled,
    settings?.isInvoiceOptionsEnabled
  );

  const isPaymentsManagedExternallyFFEnabled =
    useWorkOrdersPaymentsManagedExternallyEnabled();

  return (
    <Flex direction="column" gap={7} className={styles.billingTab}>
      <Flex direction="column" align="self-start">
        <H5 className={styles.header}>
          {t(TRANSLATION_KEYS.billingPaymentsHeader)}
        </H5>
        <Button
          variant="secondary"
          size="large"
          onClick={() =>
            history.push(
              routes.channelAdminWorkOrdersServiceRequestsBillingSettings.replace(
                ':id',
                channel._id
              )
            )
          }
        >
          {t(TRANSLATION_KEYS.productsServicesButton)}
        </Button>
      </Flex>
      <Flex direction="column" gap={4}>
        <H5 className={styles.header}>{t(TRANSLATION_KEYS.invoicesHeader)}</H5>
        {isBillingInvoicingDisabled && (
          <Alert type="warning" className={styles.alert}>
            {t(TRANSLATION_KEYS.enableInvoicingDisabledWarning)}
          </Alert>
        )}
        <Checkbox
          selected={isInvoiceOptionsEnabled}
          value={isInvoiceOptionsEnabled}
          onChange={() =>
            onSettingsUpdated({
              ...settings,
              isInvoiceOptionsEnabled: !isInvoiceOptionsEnabled,
            })
          }
          disabled={isBillingInvoicingDisabled}
          text={t(TRANSLATION_KEYS.enableInvoicingCheckbox)}
          interfaceStyle="light"
          testId="wo-invoicing-checkbox"
        />
        {isInvoiceOptionsEnabled && isPaymentsManagedExternallyFFEnabled && (
          <Checkbox
            selected={isPaymentsManagedExternally}
            value={isPaymentsManagedExternally}
            onChange={() =>
              onPaymentsManagedExternallyUpdated(!isPaymentsManagedExternally)
            }
            text={t(TRANSLATION_KEYS.paymentsManagedExternallyCheckbox)}
            interfaceStyle="light"
            testId="wo-payments-managed-externally-checkbox"
          />
        )}
      </Flex>
    </Flex>
  );
};
